import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import MuiRadio from "@mui/material/Radio";
import MuiRadioGroup from "@mui/material/RadioGroup";
import MuiTypography from "@mui/material/Typography";
import { tokens } from "../tokens";
import { cleanProps } from "../internal/cleanProps";
import { fieldErrorSx, fieldHelpSx, fieldLabelSx } from "../internal/field-styles";
import type { RadioGroupProps } from "../types";

/**
 * Containment note (types.ts): the option `Value` type is this adapter's
 * own addition (upstream `RadioGroup` carries no value generic); it is
 * bounded with `extends string`, never `unknown`.
 *
 * `variant="card"` (default) is the design system's bordered, tinted
 * option row; `variant="plain"` is `frp-radio-plain.js`'s de-carded row
 * (ADR-20260917-gh2a), used for a plain two-option yes/no question. The
 * fieldset, legend, `aria-describedby`, required state and target size all
 * come from `FormControl`/`FormLabel`/`Radio` untouched; the card wrapper
 * is this adapter's own `Box`, styled from `tokens.ts`. `afterLegend`
 * sits between the legend and the help text, inside the fieldset, for
 * content a legend may not hold (REQ-006).
 */
export function RadioGroup<Value extends string = string>({
  id,
  legend,
  afterLegend,
  help,
  "aria-describedby": ariaDescribedBy,
  value,
  onChange,
  options,
  variant = "card",
  orientation = "vertical",
  required,
  error,
  disabled,
  name,
  testId,
  errorText,
  legendVisuallyHidden,
}: Readonly<RadioGroupProps<Value>>) {
  const helpId = help !== undefined ? `${id}-help` : undefined;
  const describedBy = [helpId, ariaDescribedBy].filter((part) => part !== undefined).join(" ") || undefined;
  const plain = variant === "plain";

  // ADR-20261102-h2fd: the H2 design system's RadioGroup. `card` is the component's own bordered
  // option; `plain` is H2's `ds-radio` departure -- no card, one option per row, 3px above and
  // below each label so the 48px control keeps its target.
  return (
    <FormControl component="fieldset" sx={{ width: "100%", mb: `${sp.lg}px` }} {...cleanProps({ error, required, disabled })}>
      <FormLabel component="legend" htmlFor={id} sx={legendVisuallyHidden === true ? { ...fieldLabelSx, ...visuallyHidden } : fieldLabelSx}>
        {legend}
      </FormLabel>
      {afterLegend}
      {help !== undefined ? (
        <MuiTypography id={helpId} component="p" variant="caption" sx={fieldHelpSx}>
          {help}
        </MuiTypography>
      ) : null}
      <MuiRadioGroup
        id={id}
        row={orientation === "horizontal" && !plain}
        value={value}
        onChange={(_event, next) => {
          onChange(next as Value);
        }}
        name={name ?? id}
        {...cleanProps({ "aria-describedby": describedBy, "data-testid": testId })}
      >
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <Box key={option.value} sx={plain ? plainOptionSx : cardOptionSx(selected, orientation === "horizontal")}>
              <FormControlLabel
                value={option.value}
                control={<MuiRadio />}
                sx={{ width: "100%", m: 0, py: plain ? "3px" : `${sp.xs}px` }}
                label={
                  <Box>
                    <MuiTypography variant="body1">{option.label}</MuiTypography>
                    {option.help !== undefined ? (
                      <MuiTypography variant="caption" sx={{ display: "block", color: tokens.colour.neutral.inkMuted }}>
                        {option.help}
                      </MuiTypography>
                    ) : null}
                  </Box>
                }
                {...cleanProps({ disabled: option.disabled })}
              />
            </Box>
          );
        })}
      </MuiRadioGroup>
      {errorText !== undefined ? (
        <MuiTypography id={`${id}-error`} component="p" variant="caption" role="alert" sx={{ ...fieldErrorSx, mt: 0 }}>
          {errorText}
        </MuiTypography>
      ) : null}
    </FormControl>
  );
}

const sp = tokens.spacing;
const border = tokens.interactionState.border;

const plainOptionSx: SxProps<Theme> = { display: "flex", flexDirection: "column", alignItems: "flex-start" };

function cardOptionSx(selected: boolean, horizontal: boolean): SxProps<Theme> {
  return {
    border: `${border.controlWidth}px solid ${selected ? tokens.colour.brand[700] : tokens.colour.neutral.borderControl}`,
    borderWidth: selected ? border.emphasisWidth : border.controlWidth,
    borderRadius: `${tokens.interactionState.radius.control}px`,
    backgroundColor: selected ? tokens.colour.brand[50] : tokens.colour.neutral.white,
    mb: `${sp.xs}px`,
    px: `${sp.sm}px`,
    minHeight: tokens.interactionState.targetSize.touch,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    ...(horizontal ? { flex: 1 } : {}),
    mr: horizontal ? `${sp.xs}px` : 0,
  };
}

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
} as const;
